import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { ArrowRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import Logo from '@/components/landing/logo'
import { navLinks } from '@/constants/site'

const SiteHeader: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        const update = (): void => setScrolled(window.scrollY > 12)

        update()
        window.addEventListener('scroll', update, { passive: true })

        return () => window.removeEventListener('scroll', update)
    }, [])

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300',
                scrolled || open ? 'border-white/10 bg-background/80 backdrop-blur-xl' : 'border-transparent'
            )}
        >
            <nav className='mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8' aria-label='Main'>
                <a href='#hero' className='rounded-md' onClick={() => setOpen(false)}>
                    <Logo />
                </a>

                <ul className='hidden items-center gap-8 md:flex'>
                    {navLinks.map((link) => (
                        <li key={link.href}>
                            <a href={link.href} className='text-sm text-muted-foreground transition-colors hover:text-foreground'>
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className='flex items-center gap-2'>
                    <a
                        href='#demo'
                        className='hidden items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 sm:inline-flex'
                    >
                        Book a demo
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </a>

                    <button
                        type='button'
                        onClick={() => setOpen((current) => !current)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        className='inline-flex size-10 items-center justify-center rounded-full border border-white/10 md:hidden'
                    >
                        {open ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </nav>

            {open && (
                <div id='mobile-menu' className='border-t border-white/10 px-5 pb-6 pt-2 md:hidden'>
                    <ul className='flex flex-col'>
                        {navLinks.map((link) => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className='block py-3 text-base text-muted-foreground transition-colors hover:text-foreground'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>

                    <a
                        href='#demo'
                        onClick={() => setOpen(false)}
                        className='mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground'
                    >
                        Book a demo
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </a>
                </div>
            )}
        </header>
    )
}

export default SiteHeader
